import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { FolderInput, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { useUiStore } from '@/stores/uiStore';
import { useProjectTypes } from '@/features/regions/useRegions';
import { api, apiError } from '@/lib/api-client';

/** The "free text allowed" project value that toggles the note field. */
const PROJECT_OTHER = 'Alte';
/** Default project for new imports. */
const PROJECT_DEFAULT = 'PNERP';

type Props = { open: boolean; onClose: () => void };

type PreviewRow = {
  fileName: string;
  downloadUrl: string;
  thumbnailUrl: string;
  latitude: number | null;
  longitude: number | null;
  year: number | null;
  month: number | null;
  takenAt: string | null;
  skip: boolean;
  skipReason: string | null;
};

type PreviewResponse = {
  folderId: string;
  preview: boolean;
  summary: { total: number; importable: number; duplicates: number; missingGps: number };
  rows: PreviewRow[];
};

type CommitResponse = {
  folderId: string;
  summary: { total: number; importable: number; duplicates: number; missingGps: number; imported: number; failed: number };
  errors: Array<{ fileName: string; reason: string }>;
};

export function DriveImportDialog({ open, onClose }: Props) {
  const { t } = useTranslation(['photos', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const projectTypesQ = useProjectTypes();
  const projectOptions = projectTypesQ.data?.items.map((p) => p.name) ?? [];

  const [folderUrl, setFolderUrl] = useState('');
  const [project, setProject] = useState<string>(PROJECT_DEFAULT);
  const [projectNote, setProjectNote] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFolderUrl('');
    setProject(PROJECT_DEFAULT);
    setProjectNote('');
    setPreview(null);
    setError(null);
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const runRequest = async (commit: boolean): Promise<PreviewResponse | CommitResponse | null> => {
    setBusy(true);
    setError(null);
    try {
      const data = await api
        .post('photos/import/drive', {
          json: {
            folderUrl: folderUrl.trim(),
            project,
            projectNote:
              project === PROJECT_OTHER ? projectNote.trim() || null : null,
            commit,
          },
          timeout: 120_000,
        })
        .json<PreviewResponse | CommitResponse>();
      return data;
    } catch (err) {
      const e = await apiError(err);
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    const data = (await runRequest(false)) as PreviewResponse | null;
    if (data) setPreview(data);
  };

  const handleCommit = async () => {
    const data = (await runRequest(true)) as CommitResponse | null;
    if (!data) return;
    await qc.invalidateQueries({ queryKey: ['photos'] });
    pushToast({
      message: t('photos:drive.imported', {
        defaultValue: '{{n}} fotografii importate',
        n: data.summary.imported,
      }),
      variant: 'success',
    });
    handleClose();
  };

  const showable = preview?.rows.slice(0, 12) ?? [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('photos:drive.title', { defaultValue: 'Import din Google Drive' })}
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-500">
          {t('photos:drive.hint', {
            defaultValue:
              'Lipește un link către un folder Google Drive partajat public ("Oricine are linkul — Viewer"). Serverul citește metadatele (GPS + data) direct din Drive.',
          })}
        </p>

        <FormField
          label={t('photos:drive.folderUrl', { defaultValue: 'Link folder Drive' })}
          htmlFor="drive-folder"
        >
          <input
            id="drive-folder"
            type="url"
            value={folderUrl}
            onChange={(e) => {
              setFolderUrl(e.target.value);
              setPreview(null);
            }}
            placeholder="https://drive.google.com/drive/folders/..."
            className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm font-mono"
            autoFocus
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('photos:project', { defaultValue: 'Proiect' })} htmlFor="drive-project">
            <Select
              id="drive-project"
              value={project}
              onChange={(e) => setProject(e.target.value)}
            >
              {projectOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </FormField>
          {project === PROJECT_OTHER ? (
            <FormField
              label={t('photos:projectNote', { defaultValue: 'Notă proiect' })}
              htmlFor="drive-note"
            >
              <input
                id="drive-note"
                type="text"
                maxLength={500}
                value={projectNote}
                onChange={(e) => setProjectNote(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </FormField>
          ) : null}
        </div>

        {error ? (
          <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        {preview ? (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Stat label={t('photos:drive.total', { defaultValue: 'Total' })} value={preview.summary.total} />
              <Stat
                label={t('photos:drive.ok', { defaultValue: 'De importat' })}
                value={preview.summary.importable}
                tone="emerald"
              />
              <Stat
                label={t('photos:drive.dup', { defaultValue: 'Duplicate' })}
                value={preview.summary.duplicates}
                tone="amber"
              />
              <Stat
                label={t('photos:drive.noGps', { defaultValue: 'Fără GPS' })}
                value={preview.summary.missingGps}
                tone="red"
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded border border-slate-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1 text-left font-semibold">
                      {t('photos:table.header.fileName', { defaultValue: 'Fișier' })}
                    </th>
                    <th className="px-2 py-1 text-left font-semibold">GPS</th>
                    <th className="px-2 py-1 text-left font-semibold">
                      {t('photos:drive.when', { defaultValue: 'Dată' })}
                    </th>
                    <th className="px-2 py-1 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {showable.map((r) => (
                    <tr
                      key={r.downloadUrl}
                      className={r.skip ? 'border-t border-slate-100 text-slate-400' : 'border-t border-slate-100'}
                    >
                      <td className="max-w-[180px] truncate px-2 py-1" title={r.fileName}>
                        {r.fileName}
                      </td>
                      <td className="px-2 py-1 font-mono">
                        {r.latitude !== null && r.longitude !== null
                          ? `${r.latitude.toFixed(4)}, ${r.longitude.toFixed(4)}`
                          : '—'}
                      </td>
                      <td className="px-2 py-1">
                        {r.takenAt ? new Date(r.takenAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-2 py-1">
                        {r.skip ? (
                          <span className="inline-flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            {r.skipReason}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('photos:drive.ready', { defaultValue: 'pregătit' })}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.rows.length > showable.length ? (
                <p className="border-t border-slate-100 bg-slate-50 px-2 py-1 text-[10px] text-slate-500">
                  {t('photos:drive.moreRows', {
                    defaultValue: '{{n}} fotografii în plus nu sunt afișate',
                    n: preview.rows.length - showable.length,
                  })}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="flex justify-between gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={busy}>
            {t('common:common.cancel', { defaultValue: 'Anulează' })}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={handlePreview} disabled={!folderUrl.trim() || busy}>
              {busy && !preview ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FolderInput className="mr-1 h-4 w-4" />}
              {t('photos:drive.preview', { defaultValue: 'Previzualizează' })}
            </Button>
            <Button
              type="button"
              onClick={handleCommit}
              disabled={!preview || preview.summary.importable === 0 || busy}
            >
              {busy && preview ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('photos:drive.commit', {
                defaultValue: 'Importă {{n}}',
                n: preview?.summary.importable ?? 0,
              })}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function Stat({
  label,
  value,
  tone = 'slate',
}: {
  label: string;
  value: number;
  tone?: 'slate' | 'emerald' | 'amber' | 'red';
}) {
  const colors: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <div className={`rounded px-2 py-1.5 ${colors[tone]}`}>
      <div className="text-[9px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}
